import { Module } from 'vuex';
import has from 'lodash-es/has';
import uniqueId from 'lodash-es/uniqueId';
import { ConfirmationModalState, ModalState, Notification, NotificationType, RootState } from '../types';
import { AccessTypes } from '../../Constants';
import { documentationTabsLsKey } from '../../MultipleTabWarning';
import Enums from "Enums";
import { Routes } from "../../Routes";
import { PubSub, Subscriptions } from "../../Subscriptions";
import Overlayer from "Utils/Overlayer";
import axios from 'axios';
import { ApplicationType } from "Models/ServiceEnums";

import FacilityFinalizedVisitRolesModel = NetHealth.EHR.Domain.Models.FacilitySettings.FacilityFinalizedVisitRolesModel;
import BaseDataViewModel = NetHealth.EHR.Domain.UiModels.BaseDataViewModel;
import AppImageConfigModel = NetHealth.EHR.Domain.UiModels.AppImageConfigModel;
import DropdownOptionModel = NetHealth.EHR.Domain.Models.DropdownOptionModel;
import ComponentOfCareInfoBasicModel = NetHealth.EHR.Domain.Models.ComponentOfCareInfoBasicModel;
import VisitClassification = NetHealth.EHR.Domain.Models.Visit.VisitClassification;
import VisitTypeAuthorizationAutoSelectionConfig = NetHealth.EHR.Domain.Models.Insurance.VisitTypeAuthorizationAutoSelectionConfig;
import FacilityVisitTypeModel = NetHealth.EHR.Domain.Models.Visit.FacilityVisitTypeModel;

const buildNotification = function buildNotification(notification: Notification|string, notificationType: NotificationType): Notification {
    return typeof notification === 'string'
        ? { message: notification, notificationType}
        : {...notification as Notification, notificationType};
};

export const RootModule: Module<RootState, any> = {
    state: {
        userId: '',
        username: '',
        userEmail: '',
        uniqueUserId: '',
        userLoginId: '',
        user: {
            encUserId: '',
            firstName: '',
            middleName: '',
            lastName: '',
            billingUserId: 0,
            isIdpUser: false
        },
        userRoles: [],
        userClinicians: [],
        userComponentsOfCare: [],
        facility: {
            encFacilityId: '',
            publicFacilityID: '',
            facilityAlias: '',
            twentyFourHourTime: false,
            timeZone: '',
            parentFacilityId: '',
            billingFacilityGroupId: null,
            finalizedVisitRoles: null
        },
        facilitySettings: {
            // dynamically populated
        },
        featureToggles: {
            // dynamically populated
        },
        authorizations: {
            // dynamically populated
        },
        userSettings: {
            displayVerticalWorkflow: false,
            myReportsOpenInNewWindow: false,
        },
        modals: [],
        notifications: [],
        koFacilitySettings: {
            // copy of facility settings for KO usage
        },
        csChat: {
            chatProvider: '',
            widgetVisibility: '',
            customerStatus: '',
            haveUnreadAgentMessages: ''
        },
        visitTypeAutoAuthorizationSelectionConfig: [],
        visitClassifications: [],
        facilityVisitTypes: [],
        nhMessagingAccessUrl: '',
        nhMessagingApiUrl: '',
        appImageConfig: null,
        numberOfTabsOpened: {
            totalTabs: 1,
            documentationTabs: 0
        },
        taliSdkUrl: ''
    },
    getters: {
        userComponentOfCareItems: (state): DropdownOptionModel[] =>
            state.userComponentsOfCare.map(x => ({
                value: x.componentOfCareCode,
                text: x.componentOfCare
            })),
        userInpatientComponentOfCareItems: (state): DropdownOptionModel[] =>
            state.userComponentsOfCare.filter(item => item.isInpatientLocation).map(x => ({
                value: x.componentOfCareCode,
                text: x.componentOfCare
        })),
        userCanFinalizeVisit: (state): boolean => {
            const finalizedVisitRoles = state.facility.finalizedVisitRoles;
            const userRoles = state.userRoles;
            return finalizedVisitRoles && !!(
                (finalizedVisitRoles.finalizeVisitAdmin && userRoles.includes('FinalizeVisitUser')) ||
                (finalizedVisitRoles.facilityUser && userRoles.includes('FacilityUser')) ||
                (finalizedVisitRoles.facilityAdmin && userRoles.includes('FacilityAdmin'))
            );
        },
        userCanScheduleVisits: (state): boolean => {
            return !state.userRoles?.includes('ReadOnly') && state.userRoles?.includes('ScheduleOrStartVisit');
        },
        userCanScheduleNbis: (state): boolean => {
            return !state.userRoles?.includes('ReadOnly') && (state.userRoles?.includes('ScheduleOrStartVisit') || state.userRoles?.includes('ScheduleOrStartNbi'));
        },
        userIsNetHealthUser: (state): boolean => {
            return state.userRoles.some(x => !!x.includes('Nethealth'));
        },
        userIsReportOnlyUser: (state): boolean => {
            return state.userRoles?.every(x => x === 'ReportUser' || (x !== 'FacilityAdmin' && x !== 'ReadOnly' && x !== 'FacilityUser'));
        },
        clinicianLabel: (state): string => {
            return state.facilitySettings.reDoc
                ? (state.facilitySettings.clinicianDescription || 'Primary Therapist')
                : 'Physician';
        },
        clinicianDescription: (state): string => {
            return state.facilitySettings.reDoc
                ? (state.facilitySettings.clinicianDescription || 'clinician')
                : 'physician';
        },
        secondClinicianLabel: (state: RootState): string => {
            return state.facilitySettings.reDoc
                ? (state.facilitySettings.secondClinicianDescription || 'Secondary Therapist')
                : (state.facilitySettings.clinicianDescription || 'Clinician');
        },
        tertiaryClinicianLabel: (state: RootState): string => {
            return state.facilitySettings.reDoc ? '' : (state.facilitySettings.secondClinicianDescription || 'Second Clinician');
        },
        visitLabel: (state: RootState): string => {
            return state.facilitySettings.useEncounter ? 'Encounter' : 'Visit';
        },
        visitTypeLabel: (state: RootState, getters): string => {
            return getters.visitLabel + ' Type';
        },
        visitStatusLabel: (state: RootState, getters): string => {
            return getters.visitLabel + ' Status';
        },
        missedVisitLabel: (state: RootState, getters): string => {
            return 'Missed ' + getters.visitLabel;
        },
        appointmentVisitTypeItems: (state): DropdownOptionModel[] => {
            let visitTypeItems;
            const appointmentVisitTypes = state.facilityVisitTypes.filter(x => x.category === Enums.VisitTypeCategory.Appointment);
            if (state.facilitySettings.visitClassification) {
                visitTypeItems = state.visitClassifications
                    .filter(x => appointmentVisitTypes.some(appt => appt.visitTypeCode === x.visitTypeId))
                    .map(x => ({
                        value: x.visitClassificationId,
                        text: x.visitClassificationName
                    }));
            } else {
                visitTypeItems = appointmentVisitTypes.map(x => ({
                    value: x.visitTypeCode,
                    text: x.name
                }));
            }
            return visitTypeItems;
        },
        nonBillableInteractionVisitTypeItems: (state): DropdownOptionModel[] => {
            let visitTypeItems;
            const appointmentVisitTypes = state.facilityVisitTypes.filter(x => x.category === Enums.VisitTypeCategory.NonBillableInteraction);
            if (state.facilitySettings.visitClassification) {
                visitTypeItems = state.visitClassifications
                    .filter(x => appointmentVisitTypes.some(appt => appt.visitTypeCode === x.visitTypeId))
                    .map(x => ({
                        value: x.visitClassificationId,
                        text: x.visitClassificationName
                    }));
            } else {
                visitTypeItems = appointmentVisitTypes.map(x => ({
                    value: x.visitTypeCode,
                    text: x.name
                }));
            }
            return visitTypeItems;
        },
        appointmentVisitTypeFilterItems: (state, getters): DropdownOptionModel[] => {
            const visitTypeItems = getters.appointmentVisitTypeItems;
            if (!state.facilitySettings.reDoc) {
                return [{ text: `No ${state.facilitySettings.useEncounter ? 'Encounter' : 'Visit'} Type`, value: '-1'}, ...visitTypeItems]
            }
            return visitTypeItems;
        },
        patientDefaultImageUrl: (state) => (gender?: string): string => {
            const genderLC = !!gender ? gender.toLowerCase() : '';
            if (genderLC.startsWith('f')) return state.appImageConfig.patientDefaultFemaleImageUrl;
            if (genderLC.startsWith('m')) return state.appImageConfig.patientDefaultMaleImageUrl;
            return state.appImageConfig.patientDefaultImageUrl;
        },
        applicationId(state: RootState): string {
            return state.facilitySettings.reDoc ? ApplicationType.Therapy : ApplicationType.WoundCare;
        },
    },
    mutations: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setSettings(state: RootState, settings: {name: string; data: any}): void {
            const newSettings = {};
            Object.keys(settings.data).forEach(key =>
                // null/empty value is 'enabled', otherwise use the value
                newSettings[key] = (typeof settings.data[key] === 'undefined' || settings.data[key] === '' || settings.data[key] === null)
                    ? true : settings.data[key]
            );
            state[settings.name] = newSettings;
        },
        setFeatureToggle(state: RootState, featureToggle: {name: string; enabled: boolean}): void {
            state.featureToggles = {...state.featureToggles, [featureToggle.name]: featureToggle.enabled};
        },
        setAuthorization(state: RootState, authorization: {name: string; enabled: boolean}): void {
            state.authorizations = {...state.authorizations, [authorization.name]: authorization.enabled};
        },
        setUserId(state: RootState, data: BaseDataViewModel): void {
            state.userId = data.userId;
        },
        setVisitTypeAuthorizationAutoSelectionConfig(state: RootState, config: VisitTypeAuthorizationAutoSelectionConfig[]): void {
            state.visitTypeAutoAuthorizationSelectionConfig = config;
        },
        setUser(state: RootState, data: BaseDataViewModel): void {
            state.user = data.user;
            state.username = data.username;
            state.userEmail = data.userEmailAddress;
            state.userLoginId = data.userLoginId;
            state.uniqueUserId = data.uniqueUserId;
        },
        setUserRoles(state: RootState, data: BaseDataViewModel): void {
            state.userRoles = data.userRoles;
        },
        setUserClinicians(state: RootState, data: BaseDataViewModel): void {
            state.userClinicians = data.userClinicians;
        },
        setUserComponentsOfCare(state: RootState, data: ComponentOfCareInfoBasicModel[]): void {
            state.userComponentsOfCare = data;
        },
        setFacility(state: RootState, data: BaseDataViewModel): void {
            state.facility.encFacilityId = data.encryptedFacilityId;
            state.facility.publicFacilityID = data.publicFacilityID;
            state.facility.facilityAlias = data.facilityAlias;
            state.facility.twentyFourHourTime = data.twentyFourHourTime;
            state.facility.timeZone = data.timeZone;
            state.facility.parentFacilityId = data.parentFacilityId;
            state.facility.billingFacilityGroupId = data.billingFacilityGroupId;
        },
        setFacilityFinalizedVisitRoles(state: RootState, data: FacilityFinalizedVisitRolesModel): void {
            state.facility.finalizedVisitRoles = data;
        },
        addNotification(state: RootState, notification: Notification): void {
            state.notifications = state.notifications.concat([notification]);
        },
        deleteNotification({notifications}, index): void {
            notifications = notifications.splice(index, 1);
        },
        pushModal(state: RootState, modal: ModalState): void {
            state.modals = modal.disallowMultiple ? [modal] : state.modals.concat(modal);
        },
        popModal(state: RootState): void {
            state.modals = state.modals.slice(0, state.modals.length - 1);
        },
        removeModalByHeaderTitle(state: RootState, headerTitle: string): void {
            state.modals = state.modals.filter(x => x.headerTitle !== headerTitle);
        },
        clearModals(state: RootState): void {
            state.modals = state.modals.filter(x => x.keepOnNavigate);
        },
        setChatWidgetVisibility(state: RootState, visibility: string): void {
            state.csChat.widgetVisibility = visibility;
        },
        setChatCustomerStatus(state: RootState, status: string): void {
            state.csChat.customerStatus = status;
        },
        setUnreadAgentMessages(state: RootState, status: boolean): void {
            state.csChat.haveUnreadAgentMessages = status;
        },
        setVisitClassifications(state: RootState, data: VisitClassification[]): void {
            state.visitClassifications = data;
        },
        setFacilityVisitTypes(state: RootState, data: FacilityVisitTypeModel[]): void {
            state.facilityVisitTypes = data;
        },
        setNHMessagingAccessUrl(state: RootState, data: BaseDataViewModel): void {
            state.nhMessagingAccessUrl = data.nhMessagingAccessUrl;
        },
        setNHMessagingApiUrl(state: RootState, data: BaseDataViewModel): void {
            state.nhMessagingApiUrl = data.nhMessagingApiUrl;
        },
        setTaliSdkUrl(state: RootState, data: BaseDataViewModel): void {
            state.taliSdkUrl = data.taliSdkUrl;
        },
        setAppImageConfig(state: RootState, data: AppImageConfigModel): void {
            state.appImageConfig = data;
        },
        setTabsOpenedTotal(state: RootState, totalTabsOpened: number): void {
            state.numberOfTabsOpened.totalTabs = totalTabsOpened;
        },
        setTabsOpenedDocumentation(state: RootState, documentationTabsOpened: number): void {
            state.numberOfTabsOpened.documentationTabs = documentationTabsOpened;
            localStorage.setItem(documentationTabsLsKey, documentationTabsOpened.toString());
        }
    },
    actions: {
        initialize({commit}, data: BaseDataViewModel): void {
            commit('setUserRoles', data);
            commit('setUserClinicians', data);
            commit('setFacility', data);
            commit('setUserId', data);
            commit('setSettings', {
                name: 'userSettings',
                data: {
                    displayVerticalWorkflow: data.displayVerticalWorkflow,
                    myReportsOpenInNewWindow: data.myReportsOpenInNewWindow
                },
            });
            commit('setUser', data);
            commit('setNHMessagingAccessUrl', data);
            commit('setNHMessagingApiUrl', data);
            commit('setTaliSdkUrl', data);
            commit('setAppImageConfig', data.appImageConfig);
        },
        async loadFacilitySettings({commit, dispatch}): Promise<void> {
            const [
                settings,
                componentsOfCare,
                finalizeVisitAdminRoles,
                visitClassifications,
                facilityVisitTypes,
                visitTypeAuthorizationAutoSelectConfig
            ] = await Promise.all([
                axios.get('Access/GetAllSettings'),
                axios.get<NetHealth.EHR.Domain.Models.ComponentOfCareInfoBasicModel[]>('ComponentOfCare/UserComponentsOfCareInfoBasic'),
                axios.get<FacilityFinalizedVisitRolesModel>('Visit/Admin/FinalizeVisit', { params: { 'api-version': '2.0' } }),
                axios.get<VisitClassification[]>('Facility/VisitTypes/Classifications'),
                axios.get<FacilityVisitTypeModel[]>('Facility/VisitTypes'),
                axios.get<VisitTypeAuthorizationAutoSelectionConfig[]>('Visit/GetVisitTypeAuthorizationAutoSelections', { params: { 'api-version': '2.0' } })
            ]);

            commit('setSettings', { name: 'facilitySettings', data: settings.data });
            const lowercaseSettings = Object.keys(settings.data).reduce((results, key) =>
                (results[key.toLowerCase()] = settings.data[key], results), {});
            commit('setSettings', { name: 'koFacilitySettings', data: lowercaseSettings });
            commit('setUserComponentsOfCare', componentsOfCare.data);
            commit('setVisitTypeAuthorizationAutoSelectionConfig', visitTypeAuthorizationAutoSelectConfig?.data)
            commit('setFacilityFinalizedVisitRoles', finalizeVisitAdminRoles.data);
            commit('setVisitClassifications', visitClassifications.data);
            commit('setFacilityVisitTypes', facilityVisitTypes.data);
        },
        /*
        * @deprecated Use checkLDFeatureToggle instead for all Launchdarkly toggles
        */
        async checkFeatureToggle({commit, state}, name: string): Promise<void> {
            if (has(state.featureToggles, name)) return;
            const response = await axios.get<boolean>("Access/Authorize", { params: { actionString: name }});
            commit('setFeatureToggle', { name, enabled: response.data });
        },
        async checkAuthorization({commit, state}, name: string): Promise<void> {
            if (has(state.authorizations, name)) return;
            const response = await axios.get<boolean>("Access/Authorize", { params: { actionString: name }});
            commit('setAuthorization', { name, enabled: response.data });
        },
        async checkLDFeatureToggle({ commit, state }, data: { name: string, defaultValue: boolean }): Promise<boolean> {
            if (has(state.featureToggles, data.name)) {
                return state.featureToggles[data.name];
            }
            data.defaultValue = data.defaultValue || false; // set to false if not specified
            const response = await axios.get<boolean>("FeatureToggle/CheckFeatureToggle", { params: { toggleName: data.name, defaultValue: data.defaultValue } });
            commit('setFeatureToggle', { name: data.name, enabled: response.data });
            return response.data;
        },
        async checkLDFeatureToggleString({ commit, state }, data: { name: string, defaultValue: string }): Promise<string> {
            if (has(state.featureToggles, data.name)) {
                return state.featureToggles[data.name];
            }
            data.defaultValue = data.defaultValue || ''; // set to blank if not specified
            const response = await axios.get<string>("FeatureToggle/CheckFeatureToggleString", { params: { toggleName: data.name, defaultValue: data.defaultValue } });
            commit('setFeatureToggle', { name: data.name, enabled: response.data });
            return response.data;
        },
        async showPageOverlayAndAwait({ dispatch }, arrayOfPromises: Array<Promise<any>>): Promise<Array<any>> {
            dispatch('showPageOverlay');
            let results;
            try {
                results = await Promise.all(arrayOfPromises);
            } catch (e) {
                results = arrayOfPromises.map(x => undefined);
            }
            dispatch('hidePageOverlay');
            return results;
        },
        showPageOverlay({}, target: string): void {
            Overlayer.handleOverlay(Subscriptions.Overlay.Targetted, {
                show: true,
                showLoader: true,
                target: target || '#mainContent',
                color: 'white',
                showAsFixed: true,
                additionalCssClass: 'overlay-front'
            });
        },
        hidePageOverlay({}, target: string): void {
            Overlayer.handleOverlay(Subscriptions.Overlay.Targetted, {
                show: false,
                target: target || '#mainContent'
            });
        },
        showNotification({commit}, notification: Notification): void {
            commit('addNotification', {...notification, key: uniqueId()});
        },
        showSuccessNotification({dispatch}, notification: Notification|string): void {
            dispatch('showNotification', buildNotification(notification, NotificationType.Success));
        },
        showWarningNotification({dispatch}, notification: Notification|string): void {
            dispatch('showNotification', buildNotification(notification, NotificationType.Warning));
        },
        showErrorNotification({dispatch}, notification: Notification|string): void {
            dispatch('showNotification', buildNotification(notification, NotificationType.Error));
        },
        dismissNotification({commit, state}, notification: Notification): void {
            const index = state.notifications.indexOf(notification);
            commit('deleteNotification', index);
        },
        dismissPersistentNotifications({dispatch, state}): void {
            state.notifications.filter(x => x.persistent).forEach(x => dispatch('dismissNotification', x));
        },
        async pushModal({commit}, modal: ModalState): Promise<void> {
            return new Promise<void>((resolve) => {
                commit('pushModal', {...modal, key: uniqueId(), resolve});
            });
        },
        pushConfirmationModal({commit}, confirmModal: ConfirmationModalState): Promise<boolean> {
            return new Promise<boolean>((resolve) => {
                const modal: ModalState = {
                    key: uniqueId(),
                    name: 'Confirmation',
                    headerTitle: confirmModal.headerTitle,
                    keepOnNavigate: confirmModal.keepOnNavigate,
                    props: {
                        message: confirmModal.message,
                        itemsMessage: confirmModal.itemsMessage,
                        items: confirmModal.items,
                        showCancel: confirmModal.showCancel,
                        showClose: confirmModal.showClose,
                        showYes: confirmModal.showYes,
                        showNo: confirmModal.showNo,
                        showOk: confirmModal.showOk,
                    },
                    resolve: resolve
                };
                commit('pushModal', modal);
            });
        },
        popModal({commit}): void {
            commit('popModal');
        },
        removeModalByHeaderTitle({commit}, headerTitle: string): void {
            commit('removeModalByHeaderTitle', headerTitle);
        },
        clearModals({commit}): void {
            commit('clearModals');
        },
        async navigate({}, location: any): Promise<void> {
            const VueRouter = (await import("../../VueRouter")).default;
            VueRouter.push(location);
        },
        postIframeMessage({}, {selector, data}): boolean {
            let iframeSrc = null;
            try {
                const iframe = document.querySelector(selector) as HTMLIFrameElement;
                iframeSrc = iframe.src;
                iframe.contentWindow.postMessage(JSON.stringify(data), '*');
                return true;
            } catch (e) {
                axios.post('ClientSideException', {
                    message: `iframe postMessage error for ` + (iframeSrc ?? 'unknown iframe'),
                    url: window.location.toString(),
                    userAgent: navigator.userAgent,
                    stackTrace: 'stack' in e ? e.stack : e.toString()
                });
                return false;
            }
        },
        async logout({dispatch}): Promise<void> {
            const VueRouter = (await import("../../VueRouter")).default;
            if (VueRouter.currentRoute.name === Routes.Workflow) {
                const confirmed = await dispatch('workflow/confirmNavigation', null, {root: true});
                if (confirmed) {
                    PubSub.publish(Subscriptions.App.ConfirmLogout, {});
                }
            } else {
                PubSub.publish(Subscriptions.App.ConfirmLogout, {});
            }
        },
        setDocumentationTabCount({ commit }): void {
            const ls: number = parseInt(localStorage.getItem(documentationTabsLsKey), 10);
            const val: number = !isNaN(ls) ? ls : 0;
            commit('setTabsOpenedDocumentation', val);
        },
        alterDocumentationTabCount({ commit }, increaseCount: boolean): void {
            const ls: number = parseInt(localStorage.getItem(documentationTabsLsKey), 10);
            const val: number = !isNaN(ls) ? ls : 0;
            if (increaseCount) {
                commit('setTabsOpenedDocumentation', val + 1);
            } else {
                const newVal: number = val <= 0 ? 0 : val - 1;
                commit('setTabsOpenedDocumentation', newVal);
            }
        }
    }
};
